import {
    absenceTemplate,
    defaultIncomePeriodTemplate,
    earningsAtInjuryTemplate,
    paymentsReceivedTemplate,
    personTemplate,
    postInjuryIncomeTemplate
} from 'store/modules/shared/templates';
import {validDate} from 'components/form/validator/validations';
import Api from "services/Api";
import _ from 'lodash';
import moment from 'moment';
import store from 'store/index';
import {previousValid} from 'store/generators/step';
import Validator from 'services/Validator';
import {determineLife} from 'services/personFunctions';
import Vue from 'vue';

/**
 * Mutation Types
 *
 * @type {Object}
 */
const mutationTypes = {
    SET: 'SET',
    CLEAR_STEP: 'CLEAR_STEP',
    UPDATE_CLAIMANT_VALUE: 'UPDATE_CLAIMANT_VALUE',
    UPDATE_CLAIMANT_DETAIL: 'UPDATE_CLAIMANT_DETAIL',
    UPDATE_LIFE_EXPECTANCY: 'UPDATE_LIFE_EXPECTANCY',
    UPDATE_CLAIM_INFO_VALUE: 'UPDATE_CLAIM_INFO_VALUE',
    UPDATE_LIFE_ASSUMED_EXPECTANCY: 'UPDATE_LIFE_ASSUMED_EXPECTANCY',
    UPDATE_INCOME: 'UPDATE_INCOME',
    ADD_INCOME_PERIOD: 'ADD_INCOME_PERIOD',
    UPDATE_INCOME_PERIOD: 'UPDATE_INCOME_PERIOD',
    DELETE_INCOME_PERIOD: 'DELETE_INCOME_PERIOD',
    RESET: 'RESET',
    ADD_EARNINGS_AT_INJURY: 'ADD_EARNINGS_AT_INJURY',
    ADD_POST_INJURY_INCOME_PERIOD: 'ADD_POST_INJURY_INCOME_PERIOD',
    UPDATE_PRE_ACCIDENT_EARNINGS: 'UPDATE_PRE_ACCIDENT_EARNINGS',
    UPDATE_FUTURE_EARNINGS_PRE_ACCIDENT_EARNINGS: 'UPDATE_FUTURE_EARNINGS_PRE_ACCIDENT_EARNINGS',
    UPDATE_FUTURE_EARNINGS_POST_ACCIDENT_EARNINGS: 'UPDATE_FUTURE_EARNINGS_POST_ACCIDENT_EARNINGS',
    ADD_ABSENCE_PERIOD: 'ADD_ABSENCE_PERIOD',
    DELETE_ABSENCE_PERIOD: 'DELETE_ABSENCE_PERIOD',
    UPDATE_ABSENCE_PERIOD: 'UPDATE_ABSENCE_PERIOD',
    CLEAR_PERIODS: 'CLEAR_PERIODS',
    ADD_POST_INJURY_PAYMENT: 'ADD_POST_INJURY_PAYMENT',
    DELETE_POST_INJURY_PAYMENT: 'DELETE_POST_INJURY_PAYMENT',
    DELETE_POST_INJURY_INCOME_PERIOD: 'DELETE_POST_INJURY_INCOME_PERIOD',
    RESET_POST_INJURY_INCOME_PERIOD: 'RESET_POST_INJURY_INCOME_PERIOD',
};

/**
 * Validation Rules.
 *
 * @type {Object}
 */
let rules = {
    'theClaimant.name': {type: 'text', rules: 'required'},
    'theClaimant.gender': {type: 'text', rules: 'required'},
    'theClaimant.dateOfBirth': {type: 'date', rules: 'required'},
    'claimantDetails.claimantSolicitor': {type: 'text', rules: 'required'},
    'claimantDetails.claimantReference': {type: 'text', rules: 'required'},
    'claimantDetails.injuryType': {type: 'text', rules: 'required'},
    'claimantDetails.defendantName': {type: 'text', rules: 'required'},
    'claimantDetails.yourReference': {type: 'text', rules: 'required'},
    'claimInfo.dateOfInjury': {type: 'date', rules: 'required'},
    'claimInfo.dateOfAssumedSettlement': {type: 'date', rules: 'required'},
    'claimantDetails.claimNarrative': {type: 'text', rules: 'required'},
};

function initialState() {
    return {
        // Person
        theClaimant: personTemplate(),
        // Claimant Details
        claimantDetails: {
            claimNarrative: null,
            injuryType: null,
            injuryTypeOther: null,
            defendantName: {
                id: null,
                name: null,
                type: null
            },
            claimantSolicitor: {
                id: null,
                name: null,
                type: null
            },
            claimantReference: null,
            yourReference: null,
        },
        // Claim Info
        claimInfo: {
            discountRate: -0.25,
            claimantReductionInLife: 0,
            proceduralStatus: 'pre-lit',
            dateOfInjury: null,
            dateOfIssueProceedings: null,
            dateOfServiceProceedings: null,
            dateOfAssumedSettlement: null,
            duration: 0,
            gender: null,
        }
    };
}

/**
 * Initial State
 *
 * @type {Object}
 */
const state = initialState();

const numberVal = value => parseFloat(value || 0);

const m = value => moment(value, 'DD/MM/YYYY');

/**
 * Cycle over an object and dispatch an update for each key.
 *
 * @param {function} dispatch
 * @param {string} action
 * @param {{}} obj
 * @param {number|null} [index]
 *
 * @return {Promise}
 */
const updateObject = (dispatch, action, obj, index = null) => {
    const proms = Object.keys(obj).map(key => dispatch(action, {key, value: obj[key], index}));
    return Promise.all(proms);
};

/**
 * Find the next start date for anincome period.
 */
const findNextPeriodStart = (periods, worseCase) => {
    return periods.reduce((min, period) => {
        const periodDate = moment(period.endOfPeriod, 'DD/MM/YYYY').add(1, 'days');
        return periodDate.isAfter(min) ? periodDate : min;
    }, moment(worseCase, 'DD/MM/YYYY')).format('DD/MM/YYYY');
};

/**
 * Find the next end date for an income period.
 */
const findNextPeriodEnd = (periods, worseCase) => {
    return periods.reduce((max, period) => {
        let periodDate = moment(period.startOfPeriod, 'DD/MM/YYYY');
        return periodDate.isAfter(max) ? periodDate : max;
    }, moment(worseCase, 'DD/MM/YYYY')).format('DD/MM/YYYY');
};

/**
 * Perform a Throttle Income API request
 *
 * @param  {Function} (period, dispatch, action, args, deceased, dependant)
 * @return {Promise}
 */
const throttledIncome = _.debounce((period, theClaimant, dispatch, action, args) => Api.calculators.monetary.income(period, theClaimant)
    .then((updated) => {
        Object.keys(updated)
            .forEach((key) => {
                if (updated[key] !== null) {
                    dispatch(action, {key, value: updated[key], ...args});
                }
            });
    }), 500);

const periodHasStartAndEnd = period => {
    return period.startOfPeriod && period.endOfPeriod;
};

/**
 * Perform a Throttle Income API request
 *
 * @param  {Function} (period, dispatch, action, args, deceased, dependant)
 * @return {Promise}
 */
const throttledPostInjuryIncome = _.debounce((period, state, dispatch, action, args) =>
        Api.calculators.monetary.postInjuryIncome(period, state.theClaimant, state.claimInfo.dateOfInjury)
            .then((updated) => {
                Object.keys(updated)
                    .forEach((key) => {
                        if (updated[key] !== null) {
                            dispatch(action, {key, value: updated[key], ...args});
                        }
                    });
            })
    , 500);

/**
 * Throttle update of person life expectancy to allow for typing
 */
export const throttleUpdateLife = _.debounce((dispatch, action, person, lifeExpectancy, index) => {
    if (validDate(person.dateOfBirth)) {
        if (lifeExpectancy.method === 'assumed_percentage_of_life') {
            lifeExpectancy.method = 'percentage_of_normal_life_expectancy';
            lifeExpectancy.method = 'percentage_reduction_in_normal_life_expectancy';
        }

        let assumedSettlement = store.getters['personal/claimant/getClaimInfo']('dateOfAssumedSettlement');

        determineLife(assumedSettlement, person, lifeExpectancy)
            .then(updatedLife => {
                return updateObject(dispatch, action, updatedLife, index);
            })
    }
}, 500);

/**
 * Calculate the missing parts of assumed life expectancy
 */
const calcAssumedLifeExpectancy = ({state, assumedLifeExpectancy}) => {
    if (
        assumedLifeExpectancy.ageAtDeath
        || assumedLifeExpectancy.dateOfDeath
        || assumedLifeExpectancy.yearsLeft
        || assumedLifeExpectancy.monthsLeft
        || assumedLifeExpectancy.percentageOfNormalLifeExpectancy !== null
        || assumedLifeExpectancy.percentageReductionInNormalLifeExpectancy !== null
    ) {
        switch (assumedLifeExpectancy.method) {
            case 'assumed_years_and_months_left':
                return {
                    method: 'assumed_years_and_months_left',
                    yearsLeft: assumedLifeExpectancy.yearsLeft,
                    monthsLeft: assumedLifeExpectancy.monthsLeft,
                    dateOfDeath: moment().add(assumedLifeExpectancy.yearsLeft, 'years')
                        .add(assumedLifeExpectancy.monthsLeft, 'months')
                        .format('DD/MM/YYYY'),
                    ageAtDeath: moment().add(assumedLifeExpectancy.yearsLeft, 'years')
                        .add(assumedLifeExpectancy.monthsLeft, 'months')
                        .diff(moment(state.theClaimant.dateOfBirth, 'DD/MM/YYYY'), 'days') / 365.25,
                    percentageOfNormalLifeExpectancy: 100,
                    percentageReductionInNormalLifeExpectancy: 0,
                };
            case 'assumed_date':
                return {
                    method: 'assumed_date',
                    yearsLeft: assumedLifeExpectancy.yearsLeft,
                    monthsLeft: assumedLifeExpectancy.monthsLeft,
                    dateOfDeath: assumedLifeExpectancy.dateOfDeath,
                    ageAtDeath: moment(assumedLifeExpectancy.dateOfDeath, 'DD/MM/YYYY')
                        .diff(moment(state.theClaimant.dateOfBirth, 'DD/MM/YYYY'), 'days') / 365.25,
                    percentageOfNormalLifeExpectancy: 100,
                    percentageReductionInNormalLifeExpectancy: 0,
                };
            case 'assumed_age_at_death':
                let assumedDateDiff = moment(state.theClaimant.dateOfBirth, 'DD/MM/YYYY')
                    .add(assumedLifeExpectancy.ageAtDeath, 'years')
                    .diff(moment(), 'months');

                return {
                    method: 'assumed_age_at_death',
                    yearsLeft: Math.floor(assumedDateDiff / 12),
                    monthsLeft: assumedDateDiff - (Math.floor(assumedDateDiff / 12) * 12),
                    dateOfDeath: moment(state.theClaimant.dateOfBirth, 'DD/MM/YYYY')
                        .add(assumedLifeExpectancy.ageAtDeath, 'years').format('DD/MM/YYYY'),
                    ageAtDeath: assumedLifeExpectancy.ageAtDeath,
                    percentageOfNormalLifeExpectancy: 100,
                    percentageReductionInNormalLifeExpectancy: 0,
                };
            case 'assumed_percentage_of_life':
                assumedLifeExpectancy.percentageOfNormalLifeExpectancy = assumedLifeExpectancy.percentageOfNormalLifeExpectancy || 0;

                return {
                    method: 'assumed_percentage_of_life',
                    yearsLeft: assumedLifeExpectancy.yearsLeft,
                    monthsLeft: assumedLifeExpectancy.monthsLeft,
                    dateOfDeath: assumedLifeExpectancy.dateOfDeath,
                    ageAtDeath: assumedLifeExpectancy.ageAtDeath,
                    percentageOfNormalLifeExpectancy: assumedLifeExpectancy.percentageOfNormalLifeExpectancy,
                    percentageReductionInNormalLifeExpectancy: 100 - assumedLifeExpectancy.percentageOfNormalLifeExpectancy,
                };
            case 'assumed_reduction_in_life':
                assumedLifeExpectancy.percentageReductionInNormalLifeExpectancy = assumedLifeExpectancy.percentageReductionInNormalLifeExpectancy || 0;

                return {
                    method: 'assumed_reduction_in_life',
                    yearsLeft: assumedLifeExpectancy.yearsLeft,
                    monthsLeft: assumedLifeExpectancy.monthsLeft,
                    dateOfDeath: assumedLifeExpectancy.dateOfDeath,
                    ageAtDeath: assumedLifeExpectancy.ageAtDeath,
                    percentageOfNormalLifeExpectancy: 100 - assumedLifeExpectancy.percentageReductionInNormalLifeExpectancy,
                    percentageReductionInNormalLifeExpectancy: assumedLifeExpectancy.percentageReductionInNormalLifeExpectancy,
                };
        }
    }

    return assumedLifeExpectancy;
};

/**
 * Check that all post injury payments are valid
 */
const postInjuryIncomePeriodPaymentsValid = (state) => {
    let payments = state.theClaimant.income.postInjuryPeriods.map(period => {
        return period.paymentsReceived.reduce((valid, value) => valid ? (
            value.total !== "" &&
            value.total !== 0 &&
            value.date !== "" &&
            moment(value.date, 'DD/MM/YYYY').isSameOrAfter(moment(state.claimInfo.dateOfInjury, 'DD/MM/YYYY'))
        ) : false, true)
    });

    return payments.reduce((valid, value) => valid ? (value === true) : false, true);
};

/**
 * Check that all post injury payments are valid
 */
const postInjuryIncomePeriodsValid = (state) => {
    let periods = state.theClaimant.income.postInjuryPeriods.map(period => {
        if (period.employmentType !== 'absence') {
            return (
                period.startOfPeriod !== ""
                && period.endOfPeriod !== ""
                && (period.employmentIncome !== "" || period.employmentIncome !== 0)
                && period.totalNetIncome > 0
                && period.totalNetLoss > 0
                && period.employmentType !== ""
            )
        }
        return (
            period.startOfPeriod !== ""
            && period.endOfPeriod !== ""
            && period.employmentType !== ""
        )
    });

    return periods.reduce((valid, value) => valid ? (value === true) : false, true);
};

/**
 * Check that all future earnings are valid
 */
const futureEarningsValid = (state) => {
    let preAccidentPeriods = state.theClaimant.income.futureEarningsPreAccident;
    let preAccidentPeriodsValid = (preAccidentPeriods.totalNetIncome >= 0 && preAccidentPeriods.employmentIncome >= 0);

    let postAccidentPeriods = state.theClaimant.income.futureEarningsPostAccident;
    let postAccidentPeriodsValid = (postAccidentPeriods.totalNetIncome >= 0 && postAccidentPeriods.employmentIncome >= 0);

    return preAccidentPeriodsValid && postAccidentPeriodsValid;
};

/**
 *
 * Check that all post injury payments are valid
 */
const futureIncomePeriodsValid = (state) => {
    let periods = state.theClaimant.income.futurePeriods.map(period => {
        return (
            period.startOfPeriod !== ""
            && period.endOfPeriod !== ""
            && (period.employmentIncome !== "" || period.employmentIncome !== 0)
            && period.totalNetIncome >= 0
            && moment(period.startOfPeriod, 'DD/MM/YYYY').isBefore(moment(period.endOfPeriod, 'DD/MM/YYYY'))
        )
    });

    return periods.reduce((valid, value) => valid ? (value === true) : false, true);
};

/**
 * Get the total of payments made
 */
const postInjuryIncomePeriodPaymentsTotal = (state) => {
    return state.theClaimant.income.postInjuryPeriods.map((period, index) => {
        return state.theClaimant.income.postInjuryPeriods[index].paymentsReceived.reduce((total, carry) => total + carry.total, 0);
    }).reduce((total, payment) => {
        return total + payment;
    }, 0);
};

/**
 * Does the claimant retire after injury and before assumed settlement
 */
const retiredBetweenInjuryAndSettlement = (state) =>
    moment(state.theClaimant.income.retirementDate, 'DD/MM/YYYY')
        .isBefore(moment(state.claimInfo.dateOfAssumedSettlement, 'DD/MM/YYYY'));

/**
 * Getters.
 *
 * @type {Object}
 */
const getters = {
    claimant: state => state.theClaimant,
    claimInfo: state => state.claimInfo,
    getClaimantValue: state => key => state.theClaimant[key],
    getClaimantDetail: state => key => state.claimantDetails[key],
    getClaimInfo: state => key => state.claimInfo[key],
    getIncomeValue: state => key => state.theClaimant.income[key],
    getIncomePeriod: state => ({index, type}) => state.theClaimant.income[type + 'Periods'][index],
    getPreAccidentNetTotal: state => state.theClaimant.income.preAccident.totalNetIncome,
    getPostInjuryIncomePeriods: state => state.theClaimant.income.postInjuryPeriods,
    getPostInjuryIncomePeriodPayments: state => ({index}) => state.theClaimant.income.postInjuryPeriods[index].paymentsReceived,
    getPostInjuryIncomePeriodPaymentsTotal: state => postInjuryIncomePeriodPaymentsTotal(state),
    postInjuryIncomePeriodPaymentsValid: state => postInjuryIncomePeriodPaymentsValid(state),
    postInjuryIncomePeriodsValid: state => postInjuryIncomePeriodsValid(state),
    futureEarningsValid: state => futureEarningsValid(state),
    getAllFutureLossOfIncomeTotal: state => state.theClaimant.income.futurePeriods.reduce((total, future) => total + numberVal(future.totalNetIncome), 0),
    getPeriodsOfReducedEarningsTotal: state => state.theClaimant.income.pastPeriods.reduce((total, past) => total + numberVal(past.totalNetIncome), 0),
    futureIncomePeriodsValid: state => futureIncomePeriodsValid(state),
    pastPeriodsSubTotal: state => {
        const preAccident = state.theClaimant.income.preAccident.totalNetIncome;
        const calcPeriod = period => (preAccident - (period.totalNetIncome || 0)) * period.duration;
        const sumPeriods = (total, period) => total + calcPeriod(period);
        return state.theClaimant.income.pastPeriods.reduce(sumPeriods, 0);
    },
    pastIncomeTotal: (state, getters) => getters.pastAbsenceTotal + getters.pastPeriodsSubTotal,
    retiredBetweenInjuryAndSettlement: state => retiredBetweenInjuryAndSettlement(state),
};

// Actions
const actions = {
    /**
     * Save the step by submitting to the server.
     */
    save({getters, dispatch, state}, payload) {

        // We might change this to something which
        // is a bit cleaner...
        payload.data = state;

        return Api.claims.save(payload).then((response) => {
            return response;
        });
    },
    /**
     * Loads the step being received from the Server, to be placed in the state.
     */
    load({dispatch, action, commit}, step) {
        const data = _.deeply(_.mapKeys)(step, (value, key) => _.camelCase(key)).data;
        const theClaimant = data.theClaimant;
        const claimantDetails = data.claimantDetails;
        const claimInfo = data.claimInfo;
        theClaimant.income.ownPastLosses = !!theClaimant.income.ownPastLosses;
        theClaimant.income.ownFutureLosses = !!theClaimant.income.ownFutureLosses;

        commit(mutationTypes.SET, {key: 'theClaimant', value: theClaimant});
        commit(mutationTypes.SET, {key: 'claimantDetails', value: claimantDetails});
        commit(mutationTypes.SET, {key: 'claimInfo', value: claimInfo});
    },
    /**
     * Performs any processing once the data from the server has been stored.
     * This is a good place to do calculations or dispatch other actions which
     * are used to refresh/update calculated data on other steps/components.
     */
    process({dispatch}) {
        dispatch('personal/step/setStepNumberValid', {
            stepNo: 0,
            valid: true, // needs updating with real validity
        }, {root: true});
    },
    /**
     * Validate the step.
     *
     * @param commit
     * @param rootState
     */
    validate({commit, rootState}) {
        let updateRules = _.clone(rules);
        if (rootState.user.user.hideNames) {
            delete rules['theClaimant.name'];
        }
        let validity = (new Validator(state, updateRules)).validate();

        store.dispatch('personal/step/setStepNumberValid', {
            stepNo: 0,
            valid: previousValid(rootState, 0) && validity,
        });
    },
    /**
     * Resets the step back to defaults
     */
    reset({commit}) {
        commit(mutationTypes.RESET);
    },
    /**
     * Update a piece of Claimant data.
     */
    updateClaimantValue({commit, dispatch}, {key, value}) {
        commit(mutationTypes.UPDATE_CLAIMANT_VALUE, {key, value});

        if (key === "gender") {
            commit(mutationTypes.UPDATE_CLAIM_INFO_VALUE, {key, value});
        }

        if (key === 'dateOfBirth' || key === "gender") {
            return Promise.all([
                dispatch('updateRetirementAgeAndDate'),
                dispatch('updateLifeExpectancies')
            ]);
        }
    },
    /**
     * Update a piece of Claimant's Details data.
     */
    updateClaimantDetail({commit}, {key, value}) {
        commit(mutationTypes.UPDATE_CLAIMANT_DETAIL, {key, value});
    },
    /**
     * Update a piece of Claimant's Details data.
     */
    updateClaimInfo({state, commit, dispatch, rootGetters}, {key, value}) {
        commit(mutationTypes.UPDATE_CLAIM_INFO_VALUE, {key, value});

        if (key === 'dateOfAssumedSettlement' || key === 'dateOfInjury') {
            let startDate = state.claimInfo.dateOfInjury;
            let endDate = state.claimInfo.dateOfAssumedSettlement;
            if (!startDate || !endDate) {
                return;
            }
            dispatch('accidentToSettlementDuration', {startDate, endDate})
                .then((data) => {
                    let duration = Number((data.years.split(" ")[0]));
                    commit(mutationTypes.UPDATE_CLAIM_INFO_VALUE, {key: 'duration', value: duration});
                })
        }

        if (key === 'dateOfInjury') {
            dispatch('updateDateOfInjuryCare');
            dispatch('updateDateOfInjuryPastLossOfEarnings', value);
        }

        if (key === 'dateOfAssumedSettlement') {
            dispatch('updateDateOfAssumedSettlementCare', value);
            dispatch('updateDateOfAssumedSettlementPastLossOfEarnings', value);
            dispatch('updateDateOfAssumedSettlementFutureLosses', value);
        }

        if (key === "discountRate" && rootGetters['personal/futureLosses/shouldUpdatedFutureLosses']) {
            dispatch('personal/futureLosses/updateDiscountFactors', null, {root: true})
                .then(() => dispatch('updateFutureLossesEnd'))
                .then(() => Promise.all(state.theClaimant.income.futurePeriods.map((period, index) => dispatch('getFutureLossOfEarningsMultiplier', index))))
                .then(() => Promise.all([
                    dispatch('personal/otherFutureLosses/refresh', null, {root: true}),
                    // dispatch('personal/lossOfPension/calculatePensionLoss', null, {root: true}),
                    dispatch('personal/futureLosses/updateFutureLossOfEarnings', null, {root: true}),
                    dispatch('personal/futureLosses/updateResults', null, {root: true})
                ]));
        }


    },
    /**
     * Update the Care period with the new Date of Injury
     *
     * @param state
     */
    updateDateOfInjuryCare({state}) {
        if (store.getters['personal/otherPastLosses/value']('careCalcType') === 'manual') {
            store.dispatch('personal/otherPastLosses/updateCarePeriodValue',
                {key: 'periodStart', value: state.claimInfo.dateOfInjury, index: 0})
                .then(store.dispatch('personal/otherPastLosses/calcCareTotalForPeriod', {index: 0}));
        }
    },
    /**
     * Update the Past Loss of Earnings with the new Date of Injury
     *
     * @param state
     * @param dispatch
     * @param value
     */
    updateDateOfInjuryPastLossOfEarnings({state, dispatch}, value) {
        if (state.theClaimant.income.pastLossOfEarnings) {
            store.dispatch('personal/step/setStepNumberValid', {
                stepNo: 2,
                valid: false,
            });

            let period = state.theClaimant.income.postInjuryPeriods[0];
            let duration = moment(period.endOfPeriod, 'DD/MM/YYYY')
                .diff(moment(value, 'DD/MM/YYYY'), 'years', true);
            let postInjuryPayments = period.paymentsReceived;

            let periodPaymentsTotal = postInjuryPayments.reduce((total, carry) => total + carry.total, 0);

            period.totalNetLoss = (duration * state.theClaimant.income.preAccident.totalNetIncome) -
                (duration * period.totalNetIncome) - periodPaymentsTotal || 0;

            dispatch('updateIncomePeriod', {index: 0, period, type: 'postInjury'})
        }
    },
    /**
     * Update the Care period with the new Date of Assumed Settlement
     *
     * @param value
     */
    updateDateOfAssumedSettlementCare(value) {
        if (store.getters['personal/otherPastLosses/value']('careCalcType') === 'manual') {
            let count = store.getters['personal/otherPastLosses/value']('periods').length;
            let period = store.getters['personal/otherPastLosses/value']('periods')[count - 1];

            if (moment(period.periodEnd, 'DD/MM/YYYY').isAfter(moment(value, 'DD/MM/YYY'))) {
                store.dispatch('personal/otherPastLosses/updateCarePeriodValue',
                    {key: 'periodEnd', value: value, index: count - 1})
                    .then(store.dispatch('personal/otherPastLosses/calcCareTotalForPeriod', {index: count - 1}));
            }
        }
    },
    /**
     * Update the Past Loss of Earnings with the new Date of Assumed Settlement
     *
     * @param state
     * @param dispatch
     * @param value
     */
    updateDateOfAssumedSettlementPastLossOfEarnings({state, dispatch}, value) {
        if (state.theClaimant.income.pastLossOfEarnings) {
            store.dispatch('personal/step/setStepNumberValid', {
                stepNo: 2,
                valid: false,
            });

            let count = state.theClaimant.income.postInjuryPeriods.length;
            let period = state.theClaimant.income.postInjuryPeriods[count - 1];
            let duration = moment(value, 'DD/MM/YYYY').diff(moment(period.startOfPeriod, 'DD/MM/YYYY'), 'years', true);
            let postInjuryPayments = period.paymentsReceived;
            let periodPaymentsTotal = postInjuryPayments.reduce((total, carry) => total + carry.total, 0);

            period.totalNetLoss = (duration * state.theClaimant.income.preAccident.totalNetIncome) -
                (duration * period.totalNetIncome) - periodPaymentsTotal || 0;

            period.endOfPeriod = value;

            dispatch('updateIncomePeriod', {index: count - 1, period, type: 'postInjury'})
        }
    },
    /**
     * Update the Future Loss with the new Date of Assumed Settlement
     *
     * @param state
     * @param dispatch
     * @param value
     */
    updateDateOfAssumedSettlementFutureLosses({state, dispatch}, value) {
        if (store.getters['personal/futureLosses/getLoss']('futureLosses')) {
            store.dispatch('personal/step/setStepNumberValid', {
                stepNo: 4,
                valid: false,
            });
            store.dispatch('personal/futureLosses/updateLoss', {key: 'futureLossesStart', value}).then(
                store.dispatch('personal/futureLosses/updateFutureLossOfEarnings')
            );

            let period = state.theClaimant.income.futurePeriods[0];
            period.startOfPeriod = value;
            dispatch('updatePostInjuryIncomePeriod', {index: 0, period, type: 'future'});
        }
    },
    /**
     * Update the Claimant's Income data.
     */
    updateClaimantIncome({dispatch}, {income}) {
        return updateObject(dispatch, 'updateIncomeValue', income)
    },

    updateFutureEarningsPreAccidentEarnings({state, dispatch}) {
        let period = {
            employmentIncome: state.theClaimant.income.futureEarningsPreAccident.employmentIncome,
            employmentNetGross: state.theClaimant.income.futureEarningsPreAccident.employmentNetGross,
            startOfPeriod: state.claimInfo.dateOfAssumedSettlement,
            endOfPeriod: moment(state.claimInfo.dateOfAssumedSettlement, 'DD/MM/YYYY').add(1, 'y').format('DD/MM/YYYY'),
        };

        throttledIncome(period, state.theClaimant, dispatch, 'updateFutureEarningsPreAccidentEarningsValue');
    },
    updateFutureEarningsPreAccidentEarningsValue({commit, dispatch}, payload) {
        commit(mutationTypes.UPDATE_FUTURE_EARNINGS_PRE_ACCIDENT_EARNINGS, payload);

        if (payload.key === 'employmentNetGross') {
            dispatch('updateFutureEarningsPostAccidentEarnings');
        }
    },

    updateFutureEarningsPostAccidentEarnings({state, dispatch}) {
        let period = {
            employmentIncome: state.theClaimant.income.futureEarningsPostAccident.employmentIncome,
            employmentNetGross: state.theClaimant.income.futureEarningsPostAccident.employmentNetGross,
            startOfPeriod: state.claimInfo.dateOfAssumedSettlement,
            endOfPeriod: moment(state.claimInfo.dateOfAssumedSettlement, 'DD/MM/YYYY').add(1, 'y').format('DD/MM/YYYY'),
        };

        throttledIncome(period, state.theClaimant, dispatch, 'updateFutureEarningsPostAccidentEarningsValue');
    },
    updateFutureEarningsPostAccidentEarningsValue({commit, dispatch}, payload) {
        commit(mutationTypes.UPDATE_FUTURE_EARNINGS_POST_ACCIDENT_EARNINGS, payload);

        if (payload.key === 'employmentNetGross') {
            dispatch('updateFutureEarningsPostAccidentEarnings');
        }
    },
    /**
     * Update a piece of the Claimant's Income data.
     */
    updateIncomeValue({commit, state, dispatch}, {key, value}) {
        if (key != null && state.theClaimant.income[key] !== value) {
            commit(mutationTypes.UPDATE_INCOME, {key, value});
        }

        if (key === 'pastLossOfEarnings' && value === false) {
            commit(mutationTypes.CLEAR_STEP);
        }
    },
    /**
     * Add an income period to the claimant
     */
    addIncomePeriod({commit, state}, type) {
        let periods = state.theClaimant.income[type + 'Periods'];
        let start = (typeof periods !== 'undefined') ? findNextPeriodStart(periods, state.claimInfo.dateOfInjury) : state.claimInfo.dateOfInjury;
        let end = (typeof periods !== 'undefined') ? findNextPeriodEnd(periods, state.claimInfo.dateOfAssumedSettlement) : state.claimInfo.dateOfAssumedSettlement;

        if (moment(start, 'DD/MM/YYYY').isAfter(moment(end, 'DD/MM/YYYY'))) {
            start = end;
        }

        if (type === 'postInjury') {
            if (retiredBetweenInjuryAndSettlement(state)) {
                end = state.theClaimant.income.retirementDate;
            }

            return commit(mutationTypes.ADD_POST_INJURY_INCOME_PERIOD, {
                type, period: postInjuryIncomeTemplate({
                    start: start,
                    end: end,
                })
            })
        }

        if (type === 'past') {
            start = findNextPeriodStart(periods, state.claimInfo.dateOfInjury);
            end = findNextPeriodEnd(periods, state.claimInfo.dateOfAssumedSettlement);
        }

        if (type === 'future') {
            start = findNextPeriodStart(periods, state.claimInfo.dateOfAssumedSettlement);
            end = findNextPeriodEnd(periods, (store.getters['personal/futureLosses/getLoss']('futureLossesEnd') || state.theClaimant.income.retirementDate));
        }

        commit(mutationTypes.ADD_INCOME_PERIOD, {
            type,
            period: defaultIncomePeriodTemplate({
                start: start ? start : "",
                end: end ? end : "",
            })
        })
    },
    /**
     * Update an income period of the claimant
     */
    updateIncomePeriod({dispatch, state}, {index, period, type}) {
        let proms = Object.keys(period)
            .map(key => dispatch('updateIncomePeriodValue', {type, key, value: period[key], index}));

        if (period.endOfPeriod === ""
            || period.endOfPeriod === null
            || period.startOfPeriod === ""
            || period.startOfPeriod === null
            || (period.employmentIncome === "" || period.pensionIncome === "")
            || (period.employmentIncome < 1 || period.pensionIncome < 1)
            || (period.employmentIncome === null || period.pensionIncome === null)) {
            return;
        }

        Promise.all(proms).then(() => {
            throttledIncome(period, state.theClaimant, dispatch, 'updateIncomePeriodValue', {index, type});
        });

    },
    /**
     * Update an income period value of the claimant
     */
    updateIncomePeriodValue({commit, state, dispatch}, {index, key, value, type}) {
        let currentPeriod = state.theClaimant.income[type + 'Periods'][index];
        if (currentPeriod[key] === value || (type === "past" && key === "duration")) {
            return;
        }
        commit(mutationTypes.UPDATE_INCOME_PERIOD, {index, key, value, type});

        if (type === 'future') {
            dispatch('getFutureLossOfEarningsMultiplier', index);
        }
    },
    /**
     * Add a post injury income period
     */
    addPostInjuryIncomePeriod({commit}) {
        commit(mutationTypes.ADD_POST_INJURY_INCOME_PERIOD);
    },
    /**
     * Remove a post injury income period
     */
    deletePostInjuryPeriod({commit}, {index}) {
        commit(mutationTypes.DELETE_POST_INJURY_INCOME_PERIOD, {index});
    },
    /**
     * Remove a post injury income period
     */
    resetPostInjuryPeriod({commit}) {
        commit(mutationTypes.RESET_POST_INJURY_INCOME_PERIOD);
    },
    /**
     * Add a post injury payment
     */
    addPostInjuryPeriodPayment({commit}, {index}) {
        commit(mutationTypes.ADD_POST_INJURY_PAYMENT, index);
    },
    /**
     * Remove a post injury payment
     */
    removePostInjuryPayment({commit}, {period, payment}) {
        commit(mutationTypes.DELETE_POST_INJURY_PAYMENT, {period, payment});
    },
    /**
     * Update an income period of the claimant
     */
    updatePostInjuryIncomePeriod({state, dispatch, commit}, {index, period, type}) {
        let proms = Object.keys(period)
            .map(key => dispatch('updateIncomePeriodValue', {type, key, value: period[key], index}));

        if ((period.startOfPeriod && period.startOfPeriod === '') ||
            (period.endOfPeriod && period.endOfPeriod === '') ||
            period.employmentIncome === 0 ||
            period.employmentType === '' ||
            moment(period.startOfPeriod, 'DD/MM/YYYY').isAfter(moment(period.endOfPeriod))
        ) {
            commit(mutationTypes.UPDATE_INCOME_PERIOD, {index, key: 'totalNetIncome', value: 0, type});
            return;
        }

        Promise.all(proms)
            .then(() => throttledPostInjuryIncome(period, state, dispatch, 'updateIncomePeriodValue', {index, type}))
    },
    /**
     * delete an income period of the claimant
     */
    deleteIncomePeriod({commit, state, dispatch}, {index, type}) {
        commit(mutationTypes.DELETE_INCOME_PERIOD, {index, type});
        if (!state.theClaimant.income[type + 'Periods'].length) {
            return dispatch('addIncomePeriod', {type});
        }
    },
    /**
     * Update the retirement date
     *
     * @param state
     * @param commit
     */
    updateRetirementAgeAndDate: _.debounce(({state, commit, dispatch}) => {
        if (!validDate(state.theClaimant.dateOfBirth) || !state.theClaimant.gender) {
            return;
        }
        return Api.calculators.dates.statePensionAge(state.theClaimant)
            .then((data) => {
                commit(mutationTypes.UPDATE_INCOME, {
                    key: 'retirementAge',
                    value: data.statePension.retirementAge
                });
                commit(mutationTypes.UPDATE_INCOME, {
                    key: 'retirementDate',
                    value: data.statePension.retirementDate
                });
                dispatch('updateFutureLossesEnd');
                dispatch('checkToUpdatePension', data);
            });
    }, 500),
    /**
     * Check to see if the pension retirement age and date need updating
     */
    checkToUpdatePension({rootState}, data) {
        if (store.getters['personal/otherFutureLosses/value']('futureLossOfPension')) {
            store.dispatch('personal/otherFutureLosses/setPension', {
                key: 'butForRetirementDate',
                value: data.statePension.retirementDate
            });
            store.dispatch('personal/otherFutureLosses/setPension', {
                key: 'butForRetirementAge',
                value: data.statePension.retirementAge
            });
        }
    },
    /**
     * Update the life expectancies
     */
    updateLifeExpectancies() {
        if (store.getters['personal/otherFutureLosses/value']('futureLossOfCare') &&
            store.getters['personal/otherFutureLosses/careValue']('lossesForLife')) {

            store.dispatch('personal/otherFutureLosses/updateLifeForCare',
                store.getters['personal/otherFutureLosses/value']('careLifeExpectancy')).then(
                store.dispatch('personal/otherFutureLosses/updateCare')
            )
        }

        if (store.getters['personal/otherFutureLosses/value']('futureLossOfServices') &&
            store.getters['personal/otherFutureLosses/servicesValue']('lossesForLife')) {

            store.dispatch('personal/otherFutureLosses/updateLifeForServices',
                store.getters['personal/otherFutureLosses/value']('servicesLifeExpectancy')).then(
                store.dispatch('personal/otherFutureLosses/updateServices')
            )
        }
    },
    /**
     * Update the future losses end date
     */
    updateFutureLossesEnd({state, dispatch}) {
        if (store.getters['personal/futureLosses/getLoss']('futureLosses')) {
            let futureLossesEnd = (store.getters['personal/futureLosses/getLoss']('futureLossesToRetirement')) ?
                state.theClaimant.income.retirementDate :
                store.getters['personal/futureLosses/getLoss']('futureLossesEnd');

            let futurePeriods = state.theClaimant.income.futurePeriods;

            dispatch('updateIncomePeriodValue', {
                index: futurePeriods.length - 1,
                key: 'endOfPeriod',
                value: futureLossesEnd,
                type: 'future'
            });

            store.dispatch('personal/futureLosses/updateLoss', {key: 'futureLossesEnd', value: futureLossesEnd})
        }
    },
    /**
     * Clear the past and future periods
     */
    clearFuturePeriods({commit, dispatch}) {
        commit(mutationTypes.CLEAR_PERIODS);
        dispatch('addIncomePeriod', 'future');
    },

    updatePreAccidentEarnings({state, dispatch}) {
        let period = {
            employmentType: state.theClaimant.income.preAccident.employmentType,
            employmentIncome: state.theClaimant.income.preAccident.employmentIncome,
            employmentNetGross: state.theClaimant.income.preAccident.employmentNetGross,
            startOfPeriod: state.claimInfo.dateOfInjury,
            endOfPeriod: state.claimInfo.dateOfAssumedSettlement
        };

        throttledIncome(period, state.theClaimant, dispatch, 'updatePreAccidentEarningsValue');
    },

    updatePreAccidentEarningsValue({commit, dispatch}, payload) {
        commit(mutationTypes.UPDATE_PRE_ACCIDENT_EARNINGS, payload);

        if (payload.key === 'employmentNetGross' || payload.key === "employmentType") {
            return dispatch("updatePreAccidentEarnings");
        }
    },

    accidentToSettlementDuration({state}, {startDate, endDate}) {
        if (state.claimInfo.dateOfAssumedSettlement && state.claimInfo.dateOfInjury) {
            let dates = {
                firstDate: startDate,
                secondDate: endDate,
                options: {
                    dp: 2,
                    includeBankHolidays: true,
                    includeFirstDay: true,
                    includeLastDay: true,
                    includeWeekends: true,
                    show: {
                        years: true,
                        months: false,
                        weeks: false,
                        days: false
                    },
                }
            };

            return Api.miniCalculators.dates.PeriodBetweenTwoDates(dates);
        }
    },

    /**
     * Update the Claimant's Life Expectancy data.
     */
    updateLifeExpectancy({dispatch, state}, {lifeExpectancy, type}) {
        return updateObject(dispatch, 'updateLifeExpectancyValue', lifeExpectancy)
            .then(() => throttleUpdateLife(dispatch, 'updateLifeExpectancyValue', state.theClaimant, lifeExpectancy));
    },

    /**
     * Update a piece of the Claimant's Life Expectancy data.
     */
    updateLifeExpectancyValue({commit, state, dispatch}, {key, value}) {
        if (state.theClaimant.lifeExpectancy[key] !== value) {
            commit(mutationTypes.UPDATE_LIFE_EXPECTANCY, {key, value});
        }

        if (key === 'dateOfDeath' && store.getters['personal/otherFutureLosses/value']('reductionInLife') === false) {
            store.dispatch('personal/otherFutureLosses/set', {
                key: 'careEndDate',
                value
            });
        }
    },

    /**
     * Update the Claimant's Assumed Life Expectancy data.
     */
    updateClaimantAssumedLifeExpectancy({dispatch, state}, {assumedLifeExpectancy}) {
        let assumedLifeExpectancyResult = calcAssumedLifeExpectancy({state, assumedLifeExpectancy});

        return updateObject(dispatch, 'updateClaimantAssumedLifeExpectancyValue', assumedLifeExpectancyResult)
            .then(() => throttleUpdateLife(dispatch, 'updateClaimantAssumedLifeExpectancyValue', state.theClaimant, assumedLifeExpectancyResult));
    },

    /**
     * Update a piece of the Claimant's Assumed Life Expectancy data.
     */
    updateClaimantAssumedLifeExpectancyValue({commit, state, dispatch}, {key, value}) {
        commit(mutationTypes.UPDATE_LIFE_ASSUMED_EXPECTANCY, {key, value});

        if (key === 'dateOfDeath' && store.getters['personal/otherFutureLosses/value']('reductionInLife') === true) {
            store.dispatch('personal/otherFutureLosses/set', {
                key: 'careEndDate',
                value
            });
        }

        dispatch('updateReductionInLife');
    },

    /**
     * Update the Reduction in Claimant's Life Expectancy
     */
    updateReductionInLife({state, dispatch}) {
        if (state.theClaimant.assumedLifeExpectancy.ageAtDeath && state.theClaimant.lifeExpectancy.ageAtDeath) {
            let claimantReductionInLife = state.theClaimant.lifeExpectancy.ageAtDeath - state.theClaimant.assumedLifeExpectancy.ageAtDeath;
            dispatch('updateClaimInfo', {key: 'claimantReductionInLife', value: claimantReductionInLife});
        }
    },

    /**
     * Get the future loss multiplier
     */
    getFutureLossOfEarningsMultiplier({state, commit, dispatch}, index) {
        let period = state.theClaimant.income.futurePeriods[index];
        let duration = moment(period.endOfPeriod, 'DD/MM/YYYY').diff(moment(period.startOfPeriod, 'DD/MM/YYYY'), 'years', true);
        let MortalityMultiplier = store.getters['personal/futureLosses/getLoss']('futureLossesMortalityMultiplier');
        let futureLossesToRetirement = store.getters['personal/futureLosses/getLoss']('futureLossesToRetirement');

        if (duration > 0 && MortalityMultiplier !== null) {
            let claimInfo = store.getters['personal/claimant/getClaimInfo'];
            let claimantInfo = store.getters['personal/claimant/getClaimantValue'];
            return Api.calculators.monetary.futureLossMultiplier({
                duration: moment(period.endOfPeriod, 'DD/MM/YYYY').diff(moment(period.startOfPeriod, 'DD/MM/YYYY'), 'years', true),
                discountRate: claimInfo('discountRate'),
                gender: claimantInfo('gender'),
                dateOfBirth: claimantInfo('dateOfBirth'),
                assumedSettlement: claimInfo('dateOfAssumedSettlement'),
                retirementAge: claimantInfo('income').retirementAge,
                futureLossesToRetirement
            }).then((data) => dispatch('updateIncomePeriodValue', {
                index,
                key: 'futureLossOfEarningsMultiplier',
                value: data.multiplier.toFixed(2),
                type: 'future'
            }));
        }
    },

    updateFutureLossOfEarningsMultiplier({state, dispatch}) {
        state.theClaimant.income.futurePeriods.forEach(function (period, index) {
            dispatch('getFutureLossOfEarningsMultiplier', index)
        })
    }
};

// Mutations
const mutations = {
    /**
     * Set an object on the state.
     */
    [mutationTypes.SET](state, payload) {
        // Might be able to do this nicer with Object.assign
        Vue.set(state, payload.key, payload.value);
    },
    /**
     * Update claimant data in the state.
     */
    [mutationTypes.UPDATE_CLAIMANT_VALUE](state, payload) {
        state.theClaimant[payload.key] = payload.value;
    },
    /**
     * Update claimant detail in the state.
     */
    [mutationTypes.UPDATE_CLAIMANT_DETAIL](state, payload) {
        state.claimantDetails[payload.key] = payload.value;
    },
    /**
     * Update claim info in the state.
     */
    [mutationTypes.UPDATE_CLAIM_INFO_VALUE](state, payload) {
        state.claimInfo[payload.key] = payload.value;
    },
    /**
     * Update claimant life expectancy in the state.
     */
    [mutationTypes.UPDATE_INCOME](state, {key, value}) {
        // state.theClaimant.income[key] = value;
        state.theClaimant.income = {
            ...state.theClaimant.income,
            ...{[key]: value}
        };
    },
    /**
     * Update claimant income in the state.
     */
    [mutationTypes.UPDATE_LIFE_EXPECTANCY](state, {key, value}) {
        state.theClaimant.lifeExpectancy[key] = value;
    },
    /**
     * Update claimant assumed life expectancy in the state.
     */
    [mutationTypes.UPDATE_LIFE_ASSUMED_EXPECTANCY](state, {key, value}) {
        state.theClaimant.assumedLifeExpectancy[key] = value;
    },
    /**
     * add claimant income period in the state.
     */
    [mutationTypes.ADD_INCOME_PERIOD](state, payload) {
        payload.period.index = state.theClaimant.income[payload.type + 'Periods'].length;
        state.theClaimant.income[payload.type + 'Periods'].push(payload.period);
    },
    /**
     * update claimant income period in the state.
     */
    [mutationTypes.UPDATE_INCOME_PERIOD](state, {key, value, index, type}) {
        let period = state.theClaimant.income[type + 'Periods'][index];
        period[key] = value;
        if ((key === "startOfPeriod" || key === "endOfPeriod") && period.startOfPeriod && period.endOfPeriod) {
            period.duration = Math.round(m(period.endOfPeriod)
                .diff(m(period.startOfPeriod), 'years', true) * 10) / 10;
        }
    },
    /**
     * delete claimant income period in the state.
     */
    [mutationTypes.DELETE_INCOME_PERIOD](state, payload) {
        state.theClaimant.income[payload.type + 'Periods'].splice(payload.index, 1);
    },
    /**
     * Reset state.
     */
    [mutationTypes.RESET](state) {
        Object.assign(state, initialState());
    },
    /**
     * Add the earnings at injury template to the past income state
     */
    [mutationTypes.ADD_EARNINGS_AT_INJURY](state) {
        state.theClaimant.income.pastPeriods.push(earningsAtInjuryTemplate());
    },
    /**
     * Add the post injury income template to the future income state
     */
    [mutationTypes.ADD_POST_INJURY_INCOME_PERIOD](state, payload) {
        payload.period.index = state.theClaimant.income[payload.type + 'Periods'].length;
        state.theClaimant.income.postInjuryPeriods.push(payload.period);
    },
    /**
     * Delete the post injury income period from the state
     */
    [mutationTypes.DELETE_POST_INJURY_INCOME_PERIOD](state, index) {
        state.theClaimant.income.postInjuryPeriods.splice(index, 1);
    },
    /**
     * Reset post injury income to initial state
     */
    [mutationTypes.RESET_POST_INJURY_INCOME_PERIOD](state) {
        state.theClaimant.income.postInjuryPeriods = [];
    },

    /**
     * Add the post injury income template to the income state
     */
    [mutationTypes.ADD_POST_INJURY_PAYMENT](state, index) {
        state.theClaimant.income.postInjuryPeriods[index].paymentsReceived.push(paymentsReceivedTemplate());
    },
    /**
     * Delete the post injury income payment from the income state
     */
    [mutationTypes.DELETE_POST_INJURY_PAYMENT](state, payload) {
        state.theClaimant.income.postInjuryPeriods[payload.period].paymentsReceived.splice(payload.payment, 1);
    },
    /**
     * Update claimant pre accident earnings in the state
     */
    [mutationTypes.UPDATE_PRE_ACCIDENT_EARNINGS](state, payload) {
        state.theClaimant.income.preAccident[payload.key] = payload.value;
    },
    /**
     * Update claimant future earnings pre accident earnings in the state
     */
    [mutationTypes.UPDATE_FUTURE_EARNINGS_PRE_ACCIDENT_EARNINGS](state, payload) {
        state.theClaimant.income.futureEarningsPreAccident[payload.key] = payload.value;
    },

    /**
     * Update claimant post accident earnings in the state
     */
    [mutationTypes.UPDATE_FUTURE_EARNINGS_POST_ACCIDENT_EARNINGS](state, payload) {
        state.theClaimant.income.futureEarningsPostAccident[payload.key] = payload.value;
    },

    [mutationTypes.ADD_ABSENCE_PERIOD](state) {
        state.theClaimant.income.absencePeriods.push(absenceTemplate());
    },

    [mutationTypes.DELETE_ABSENCE_PERIOD](state, index) {
        state.theClaimant.income.absencePeriods.splice(index, 1);
    },

    [mutationTypes.UPDATE_ABSENCE_PERIOD](state, {key, value, index}) {
        let absencePeriod = state.theClaimant.income.absencePeriods[index];
        absencePeriod[key] = value;
        if ((key === "startOfPeriod" || key === "endOfPeriod") && absencePeriod.startOfPeriod && absencePeriod.endOfPeriod) {
            absencePeriod.duration = Math.round(m(absencePeriod.endOfPeriod).diff(m(absencePeriod.startOfPeriod), 'years', true) * 10) / 10;
        }
    },
    /**
     * Update claimant income in the state.
     */
    [mutationTypes.UPDATE_LIFE_EXPECTANCY](state, {key, value}) {
        state.theClaimant.lifeExpectancy[key] = value;
    },
    /**
     * Clear the future periods
     */
    [mutationTypes.CLEAR_PERIODS](state) {
        state.theClaimant.income.futurePeriods = [];
        state.theClaimant.income.pastPeriods = [];
    },

    /**
     * Clear the future periods
     */
    [mutationTypes.CLEAR_STEP](state) {
        state.theClaimant.income.preAccident.employmentType = 'employed';
        state.theClaimant.income.preAccident.employmentIncome = 0;
        state.theClaimant.income.preAccident.employmentNetGross = 'gross';
        state.theClaimant.income.preAccident.totalNetIncome = 0;
        state.theClaimant.income.postInjuryPeriods = [];

        let periods = Object.keys(state.theClaimant.income.postInjuryPeriods);
        let start = findNextPeriodStart(periods, state.claimInfo.dateOfInjury);
        let end = findNextPeriodStart(periods, state.claimInfo.dateOfAssumedSettlement);
        Object.assign(state.theClaimant.income.postInjuryPeriods, [postInjuryIncomeTemplate({start, end})]);
    }
};

export default {
    namespaced: true,
    state,
    getters,
    actions,
    mutations
}



// WEBPACK FOOTER //
// ./src/store/modules/personal/claimant.js